import { useState, useEffect, useRef, useCallback } from 'react'

// Matches what device-stream-manager.ts actually emits over the wire — this
// type previously declared fields (userName, timestamp, membershipStatus)
// that don't exist on the real payload, which silently broke every consumer
// that trusted it (they compiled fine, since JSON.parse().as<T>() isn't
// runtime-checked, and just read `undefined` at runtime).
export type SseEvent =
  | {
      type: 'accessEvent'
      deviceId: string
      employeeNo: string | null
      name: string | null
      eventType: 'ACCESS_GRANTED' | 'ACCESS_DENIED'
      minor: number | null
      doorNo: number | null
      verifyMode: string
      occurredAt: string
    }
  | { type: 'stream_error'; deviceId: string; error: string }
  | { type: 'stream_reconnect'; deviceId: string; attempt: number; delayMs: number }

export interface SseState {
  events: SseEvent[]
  connected: boolean
}

// The server writes a `data:` ping every 20s (see stream.ts) specifically so
// this watchdog has something to measure — an idle-but-healthy connection
// still gets a message every 20s, so silence past this window means the
// connection went stale (dropped by a NAT/proxy hop, network blip) without
// either side's TCP stack noticing. EventSource's own onerror only fires once
// the OS finally notices the socket is dead, which can take much longer than
// a person waiting at the front desk is willing to — previously the only way
// to recover was refreshing the page. This forces a reconnect proactively.
const WATCHDOG_MS = 45_000

export function useSSE(proxyUrl: string | null, proxyToken: string | null, deviceIds: string[]): SseState {
  const [events, setEvents] = useState<SseEvent[]>([])
  const [connected, setConnected] = useState(false)
  const esRef = useRef<EventSource | null>(null)
  const retryRef = useRef(1000)
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const watchdogTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const connect = useCallback(() => {
    if (!proxyUrl || !proxyToken || deviceIds.length === 0) return

    const url = `${proxyUrl}/stream?devices=${deviceIds.join(',')}&proxyToken=${proxyToken}`
    const es = new EventSource(url)
    esRef.current = es

    const resetWatchdog = () => {
      if (watchdogTimer.current) clearTimeout(watchdogTimer.current)
      watchdogTimer.current = setTimeout(() => {
        es.close()
        esRef.current = null
        setConnected(false)
        connect()
      }, WATCHDOG_MS)
    }

    es.onopen = () => {
      setConnected(true)
      retryRef.current = 1000
      resetWatchdog()
    }

    es.onmessage = (e) => {
      resetWatchdog()
      try {
        const data = JSON.parse(e.data) as SseEvent | { type: 'ping'; timestamp: string }
        if (data.type === 'ping') return
        setEvents((prev) => [data, ...prev].slice(0, 50))
      } catch { /* ignore malformed */ }
    }

    es.onerror = () => {
      setConnected(false)
      es.close()
      esRef.current = null
      if (watchdogTimer.current) clearTimeout(watchdogTimer.current)
      const delay = Math.min(retryRef.current, 30000)
      retryRef.current = Math.min(retryRef.current * 2, 30000)
      retryTimer.current = setTimeout(connect, delay)
    }
  }, [proxyUrl, deviceIds.join(',')]) // eslint-disable-line

  useEffect(() => {
    if (!proxyUrl || !proxyToken) return
    connect()
    return () => {
      esRef.current?.close()
      if (retryTimer.current) clearTimeout(retryTimer.current)
      if (watchdogTimer.current) clearTimeout(watchdogTimer.current)
    }
  }, [connect, proxyUrl, proxyToken])

  return { events, connected }
}
